import React, { useState } from 'react';
import { 
  Calendar, ChevronDown, ChevronUp, Plus, Pill, Activity, 
  Heart, User, FileText, Save, Send, X, Clipboard, TestTube, 
  ArrowLeft, CheckCircle, Clock, AlertCircle, Microscope
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAuth } from '../../lib/hooks/useAuth';
import { PatientLayout } from '../patient/PatientLayout';
import { LoadingState } from '../common/LoadingState';
import { AlertCard } from '../common/AlertCard';

interface Medication {
  id: string;
  name: string;
  dosage: string;
  frequency: string;
  route: string;
  duration: string;
}

interface PostTransplantEvent {
  airwayComplications: boolean;
  airwayNotes: string;
  rejectionEvents: boolean;
  rejectionTypes: string[];
  eventDate: Date | undefined;
  comments: string;
}

interface LungBiopsy {
  performed: boolean;
  biopsyType: string;
  biopsyDate: Date | undefined;
  findings: string;
}

interface LabValues {
  rft: string;
  hemoglobin: string;
  tlc: string;
  fev1: string;
  tacLevels: string;
  labDate: Date | undefined;
  comments: string;
}

interface FormData {
  preTxDiagnosis: string;
  transplantDate: Date | undefined;
  postTransplantEvents: PostTransplantEvent;
  lungBiopsy: LungBiopsy;
  medications: Medication[];
  labValues: LabValues;
  doctorRemarks: string;
  patientCounseled: boolean;
}

interface DischargeSummaryOnboardingProps {
  patient?: {
    id: string;
    name: string;
    age: number;
    hospitalName?: string;
  };
  onBack: () => void;
  onNavigate?: (view: string) => void;
  onLogout?: () => void;
  onComplete?: (patientId: string) => void;
  useOwnLayout?: boolean; // ✅ NEW: Control whether to use PatientLayout wrapper
}

export function DischargeSummaryOnboarding({ patient, onBack, onNavigate, onLogout, onComplete, useOwnLayout = true }: DischargeSummaryOnboardingProps) {
  const { user, loading: authLoading } = useAuth();
  
  // ✅ FIX: Use patient from props or get from user - with better fallback
  const currentPatient = patient || (user ? {
    id: user.patient_id?.toString() || user.id?.toString() || '0',
    name: user.full_name || user.name || 'Patient',
    age: 0, // Would need to calculate from DOB
    hospitalName: undefined
  } : null);
  
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    demographics: true,
    events: true,
    biopsy: true,
    medications: true,
    labs: true,
    notes: true,
  });

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  // Show loading while auth is loading
  if (authLoading) {
    return (
      <PatientLayout 
        currentView="discharge-summary" 
        onNavigate={onNavigate} 
        onLogout={onLogout}
        patientName={'Patient'}
        adherenceScore={0}
      >
        <div className="max-w-7xl mx-auto px-4 py-6">
          <LoadingState message="Loading..." />
        </div>
      </PatientLayout>
    );
  }
  
  // ✅ FIX: Better error message for missing patient profile
  if (!currentPatient || (!currentPatient.name && !user)) {
    return (
      <PatientLayout 
        currentView="discharge-summary" 
        onNavigate={onNavigate} 
        onLogout={onLogout}
        patientName={user?.full_name || user?.name || 'Patient'}
        adherenceScore={user?.adherence || 0}
      >
        <div className="max-w-7xl mx-auto p-6">
          <AlertCard
            type="error"
            title="Patient Profile Not Found"
            description={
              user?.patient_id 
                ? "Your patient profile hasn't been set up yet. Please contact your care coordinator to complete your registration."
                : "You need to be logged in as a patient to access discharge summaries. Please login and try again."
            }
            action={
              <div className="flex gap-2">
                <Button onClick={onBack} variant="outline">
                  Go Back
                </Button>
                {onLogout && (
                  <Button onClick={onLogout}>
                    Logout
                  </Button>
                )}
              </div>
            }
          />
        </div>
      </PatientLayout>
    );
  }
  
  const [formData, setFormData] = useState<FormData>({
    preTxDiagnosis: '',
    transplantDate: undefined,
    postTransplantEvents: {
      airwayComplications: false,
      airwayNotes: '',
      rejectionEvents: false,
      rejectionTypes: [],
      eventDate: undefined,
      comments: '',
    },
    lungBiopsy: {
      performed: false,
      biopsyType: '',
      biopsyDate: undefined,
      findings: '',
    },
    medications: [],
    labValues: {
      rft: '',
      hemoglobin: '',
      tlc: '',
      fev1: '',
      tacLevels: '',
      labDate: undefined,
      comments: '',
    },
    doctorRemarks: '',
    patientCounseled: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const addMedication = () => {
    const newMed: Medication = {
      id: Date.now().toString(),
      name: '',
      dosage: '',
      frequency: '',
      route: '',
      duration: '',
    };
    setFormData(prev => ({
      ...prev,
      medications: [...prev.medications, newMed],
    }));
  };

  const updateMedication = (id: string, field: keyof Medication, value: string) => {
    setFormData(prev => ({
      ...prev,
      medications: prev.medications.map(med => 
        med.id === id ? { ...med, [field]: value } : med
      ),
    }));
  };

  const removeMedication = (id: string) => {
    setFormData(prev => ({
      ...prev,
      medications: prev.medications.filter(med => med.id !== id),
    }));
  };

  const handleRejectionTypeToggle = (type: string) => {
    setFormData(prev => ({
      ...prev,
      postTransplantEvents: {
        ...prev.postTransplantEvents,
        rejectionTypes: prev.postTransplantEvents.rejectionTypes.includes(type)
          ? prev.postTransplantEvents.rejectionTypes.filter(t => t !== type)
          : [...prev.postTransplantEvents.rejectionTypes, type],
      },
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.transplantDate) {
      toast.error('Transplant date is required');
      return false;
    }
    if (!formData.preTxDiagnosis) {
      toast.error('Pre-Tx Diagnosis is required');
      return false;
    }
    return true;
  };

  const handleSaveDraft = () => {
    toast.success('Draft saved successfully!');
  };

  const handleSubmit = () => {
    if (validateForm()) {
      setShowConfirmModal(true);
    }
  };

  const handleConfirm = () => {
    setShowConfirmModal(false);
    toast.success('Discharge summary completed successfully!', {
      style: {
        background: '#14CAF4',
        color: '#fff',
      },
    });
    onComplete && onComplete(patient.id);
  };

  // ✅ FIX: Main content (without layout wrapper)
  const mainContent = (
    <>
      <div className="max-w-7xl mx-auto p-3 sm:p-4 md:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <Button variant="ghost" onClick={onBack} className="mb-3 sm:mb-4 text-xs sm:text-sm h-8 sm:h-9">
            <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            Back to Onboarding List
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-primary text-lg sm:text-2xl">Discharge Summary Entry</h1>
              <p className="text-muted-foreground text-xs sm:text-sm">
                Complete onboarding for {currentPatient.name}
              </p>
            </div>
            <Badge variant="outline" className="bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13] w-fit">
              Onboarding Pending
            </Badge>
          </div>
        </div>

        {/* Patient Info Card */}
        <Card className="mb-4 sm:mb-6 border-l-4 border-l-primary">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                <span className="text-primary-foreground text-sm sm:text-base">
                  {currentPatient.name.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div>
                <h3 className="text-primary text-sm sm:text-base">{currentPatient.name}</h3>
                <p className="text-muted-foreground text-xs sm:text-sm">
                  {currentPatient.age} years {currentPatient.hospitalName && `• ${currentPatient.hospitalName}`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-6">
          <Button variant="outline" onClick={handleSaveDraft} className="gap-2 text-xs sm:text-sm">
            <Save className="w-3 h-3 sm:w-4 sm:h-4" />
            Save Draft
          </Button>
          <Button 
            className="bg-accent hover:bg-accent/90 gap-2 text-xs sm:text-sm"
            onClick={handleSubmit}
          >
            <Send className="w-3 h-3 sm:w-4 sm:h-4" />
            Complete Onboarding
          </Button>
        </div>

        {/* Section 1: Transplant Details */}
        <Card className="mb-4 border-l-4 border-l-primary">
          <CardHeader 
            className="cursor-pointer hover:bg-secondary/50 transition-colors p-3 sm:p-6"
            onClick={() => toggleSection('demographics')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                <CardTitle className="text-sm sm:text-base">Transplant Details</CardTitle>
              </div>
              {expandedSections.demographics ? 
                <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" /> : 
                <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
              }
            </div>
          </CardHeader>
          {expandedSections.demographics && (
            <CardContent className="space-y-3 sm:space-y-4 p-3 sm:p-6 bg-secondary/20">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label className="text-primary text-xs sm:text-sm">Pre-Tx Diagnosis *</Label>
                  <Select 
                    value={formData.preTxDiagnosis}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, preTxDiagnosis: value }))}
                  >
                    <SelectTrigger className="text-xs sm:text-sm">
                      <SelectValue placeholder="Select diagnosis" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="copd">COPD</SelectItem>
                      <SelectItem value="ipf">Idiopathic Pulmonary Fibrosis</SelectItem>
                      <SelectItem value="cf">Cystic Fibrosis</SelectItem>
                      <SelectItem value="pah">Pulmonary Arterial Hypertension</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-primary text-xs sm:text-sm">Transplant Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left text-xs sm:text-sm"
                      >
                        <Calendar className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                        {formData.transplantDate ? format(formData.transplantDate, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarComponent
                        mode="single"
                        selected={formData.transplantDate}
                        onSelect={(date) => setFormData(prev => ({ ...prev, transplantDate: date }))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Section 2: Post-Transplant Events */}
        <Card className="mb-4 border-l-4 border-l-accent">
          <CardHeader 
            className="cursor-pointer hover:bg-secondary/50 transition-colors p-3 sm:p-6"
            onClick={() => toggleSection('events')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-accent" />
                <CardTitle className="text-sm sm:text-base">Post-Transplant Events</CardTitle>
              </div>
              {expandedSections.events ? 
                <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" /> : 
                <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
              }
            </div>
          </CardHeader>
          {expandedSections.events && (
            <CardContent className="space-y-4 sm:space-y-6 p-3 sm:p-6">
              {/* Airway Complications */}
              <div className="space-y-3 p-3 sm:p-4 rounded-lg bg-background border">
                <div className="flex items-center justify-between">
                  <Label className="text-xs sm:text-sm">Airway Complications</Label>
                  <Switch 
                    checked={formData.postTransplantEvents.airwayComplications}
                    onCheckedChange={(checked) => 
                      setFormData(prev => ({
                        ...prev,
                        postTransplantEvents: { ...prev.postTransplantEvents, airwayComplications: checked }
                      }))
                    }
                  />
                </div>
                {formData.postTransplantEvents.airwayComplications && (
                  <Textarea 
                    placeholder="Enter notes about airway complications..."
                    value={formData.postTransplantEvents.airwayNotes}
                    onChange={(e) => 
                      setFormData(prev => ({
                        ...prev,
                        postTransplantEvents: { ...prev.postTransplantEvents, airwayNotes: e.target.value }
                      }))
                    }
                    className="mt-2 text-xs sm:text-sm"
                  />
                )}
              </div>

              {/* Rejection Events */}
              <div 
                className="space-y-3 p-3 sm:p-4 rounded-lg border"
                style={{ 
                  backgroundColor: formData.postTransplantEvents.rejectionEvents ? '#FEEBDA' : 'transparent'
                }}
              >
                <div className="flex items-center justify-between">
                  <Label className="text-xs sm:text-sm">Rejection Events</Label>
                  <Switch 
                    checked={formData.postTransplantEvents.rejectionEvents}
                    onCheckedChange={(checked) => 
                      setFormData(prev => ({
                        ...prev,
                        postTransplantEvents: { ...prev.postTransplantEvents, rejectionEvents: checked }
                      }))
                    }
                  />
                </div>
                
                {formData.postTransplantEvents.rejectionEvents && (
                  <div className="space-y-3 sm:space-y-4 mt-4">
                    <div>
                      <Label className="mb-2 block text-xs sm:text-sm">Rejection Types</Label>
                      <div className="flex flex-wrap gap-2">
                        {['ACR', 'AMR', 'CLAD'].map((type) => (
                          <Badge
                            key={type}
                            variant={formData.postTransplantEvents.rejectionTypes.includes(type) ? 'default' : 'outline'}
                            className="cursor-pointer px-2 sm:px-3 py-1 text-xs"
                            style={{
                              backgroundColor: formData.postTransplantEvents.rejectionTypes.includes(type) ? '#F07D13' : 'transparent',
                              color: formData.postTransplantEvents.rejectionTypes.includes(type) ? '#fff' : '#494949',
                            }}
                            onClick={() => handleRejectionTypeToggle(type)}
                          >
                            {type}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs sm:text-sm">Event Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left text-xs sm:text-sm"
                          >
                            <Calendar className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                            {formData.postTransplantEvents.eventDate ? 
                              format(formData.postTransplantEvents.eventDate, 'PPP') : 
                              'Pick a date'
                            }
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <CalendarComponent
                            mode="single"
                            selected={formData.postTransplantEvents.eventDate}
                            onSelect={(date) => 
                              setFormData(prev => ({
                                ...prev,
                                postTransplantEvents: { ...prev.postTransplantEvents, eventDate: date }
                              }))
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs sm:text-sm">Comments</Label>
                      <Textarea 
                        placeholder="Enter comments about rejection events..."
                        value={formData.postTransplantEvents.comments}
                        onChange={(e) => 
                          setFormData(prev => ({
                            ...prev,
                            postTransplantEvents: { ...prev.postTransplantEvents, comments: e.target.value }
                          }))
                        }
                        rows={3}
                        className="text-xs sm:text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          )}
        </Card>

        {/* Section 3: Lung Biopsy */}
        <Card className="mb-4 border-l-4 border-l-[#14CAF4]">
          <CardHeader 
            className="cursor-pointer hover:bg-secondary/50 transition-colors p-3 sm:p-6"
            onClick={() => toggleSection('biopsy')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Microscope className="w-4 h-4 sm:w-5 sm:h-5 text-[#14CAF4]" />
                <CardTitle className="text-sm sm:text-base">Lung Biopsy</CardTitle>
              </div>
              {expandedSections.biopsy ? 
                <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" /> : 
                <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
              }
            </div>
          </CardHeader>
          {expandedSections.biopsy && (
            <CardContent className="space-y-3 sm:space-y-4 p-3 sm:p-6">
              <div className="space-y-3 p-3 sm:p-4 rounded-lg bg-[#D4F0F8]/30 border border-[#14CAF4]/30">
                <div className="flex items-center justify-between">
                  <Label className="text-primary text-xs sm:text-sm">Lung Biopsy Performed</Label>
                  <Switch 
                    checked={formData.lungBiopsy.performed}
                    onCheckedChange={(checked) => 
                      setFormData(prev => ({
                        ...prev,
                        lungBiopsy: { ...prev.lungBiopsy, performed: checked }
                      }))
                    }
                  />
                </div>
                
                {formData.lungBiopsy.performed && (
                  <div className="space-y-3 mt-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-primary text-xs sm:text-sm">Biopsy Type</Label>
                        <Select 
                          value={formData.lungBiopsy.biopsyType}
                          onValueChange={(value) => 
                            setFormData(prev => ({
                              ...prev,
                              lungBiopsy: { ...prev.lungBiopsy, biopsyType: value }
                            }))
                          }
                        >
                          <SelectTrigger className="text-xs sm:text-sm">
                            <SelectValue placeholder="Select biopsy type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="transbronchial">Transbronchial Biopsy</SelectItem>
                            <SelectItem value="surgical">Surgical Lung Biopsy</SelectItem>
                            <SelectItem value="needle">CT-Guided Needle Biopsy</SelectItem>
                            <SelectItem value="open">Open Lung Biopsy</SelectItem>
                            <SelectItem value="vats">VATS (Video-Assisted Thoracoscopic Surgery)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-primary text-xs sm:text-sm">Biopsy Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-start text-left text-xs sm:text-sm"
                            >
                              <Calendar className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                              {formData.lungBiopsy.biopsyDate ? 
                                format(formData.lungBiopsy.biopsyDate, 'PPP') : 
                                'Pick a date'
                              }
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <CalendarComponent
                              mode="single"
                              selected={formData.lungBiopsy.biopsyDate}
                              onSelect={(date) => 
                                setFormData(prev => ({
                                  ...prev,
                                  lungBiopsy: { ...prev.lungBiopsy, biopsyDate: date }
                                }))
                              }
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-primary text-xs sm:text-sm">Biopsy Findings</Label>
                      <Textarea 
                        placeholder="Enter detailed biopsy findings and pathology report summary..."
                        value={formData.lungBiopsy.findings}
                        onChange={(e) => 
                          setFormData(prev => ({
                            ...prev,
                            lungBiopsy: { ...prev.lungBiopsy, findings: e.target.value }
                          }))
                        }
                        rows={3}
                        className="text-xs sm:text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          )}
        </Card>

        {/* Section 4: Medications */}
        <Card className="mb-4 border-l-4 border-l-[#14CAF4]">
          <CardHeader 
            className="cursor-pointer hover:bg-secondary/50 transition-colors p-3 sm:p-6"
            onClick={() => toggleSection('medications')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Pill className="w-4 h-4 sm:w-5 sm:h-5 text-[#14CAF4]" />
                <CardTitle className="text-sm sm:text-base">Medications at Discharge</CardTitle>
              </div>
              {expandedSections.medications ? 
                <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" /> : 
                <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
              }
            </div>
          </CardHeader>
          {expandedSections.medications && (
            <CardContent className="space-y-3 sm:space-y-4 p-3 sm:p-6">
              {formData.medications.length === 0 ? (
                <div className="text-center py-6 sm:py-8 text-muted-foreground">
                  <Pill className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 opacity-30" />
                  <p className="text-xs sm:text-sm">No medications added yet</p>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {formData.medications.map((med, index) => (
                    <div key={med.id} className="p-3 sm:p-4 border rounded-lg bg-background space-y-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs sm:text-sm text-primary">
                          Medication {index + 1}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeMedication(med.id)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10 h-7 w-7 p-0"
                        >
                          <X className="w-3 h-3 sm:w-4 sm:h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Medication Name</Label>
                          <Input 
                            value={med.name}
                            onChange={(e) => updateMedication(med.id, 'name', e.target.value)}
                            placeholder="e.g., Tacrolimus"
                            className="text-xs sm:text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Dosage</Label>
                          <Input 
                            value={med.dosage}
                            onChange={(e) => updateMedication(med.id, 'dosage', e.target.value)}
                            placeholder="e.g., 5mg"
                            className="text-xs sm:text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Frequency</Label>
                          <Input 
                            value={med.frequency}
                            onChange={(e) => updateMedication(med.id, 'frequency', e.target.value)}
                            placeholder="e.g., Twice daily"
                            className="text-xs sm:text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Route</Label>
                          <Select 
                            value={med.route}
                            onValueChange={(value) => updateMedication(med.id, 'route', value)}
                          >
                            <SelectTrigger className="text-xs sm:text-sm">
                              <SelectValue placeholder="Select route" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="oral">Oral</SelectItem>
                              <SelectItem value="iv">Intravenous</SelectItem>
                              <SelectItem value="im">Intramuscular</SelectItem>
                              <SelectItem value="sc">Subcutaneous</SelectItem>
                              <SelectItem value="inhaled">Inhaled</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Duration</Label>
                          <Input 
                            value={med.duration}
                            onChange={(e) => updateMedication(med.id, 'duration', e.target.value)}
                            placeholder="e.g., 30 days"
                            className="text-xs sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <Button 
                variant="outline" 
                onClick={addMedication}
                className="w-full gap-2 border-primary text-primary hover:bg-primary/10 text-xs sm:text-sm"
              >
                <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                Add Medication
              </Button>
            </CardContent>
          )}
        </Card>

        {/* Section 5: Laboratory Values */}
        <Card className="mb-4 border-l-4 border-l-[#494949]">
          <CardHeader 
            className="cursor-pointer hover:bg-secondary/50 transition-colors p-3 sm:p-6"
            onClick={() => toggleSection('labs')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TestTube className="w-4 h-4 sm:w-5 sm:h-5 text-[#494949]" />
                <CardTitle className="text-sm sm:text-base">Latest Laboratory Results</CardTitle>
              </div>
              {expandedSections.labs ? 
                <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" /> : 
                <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
              }
            </div>
          </CardHeader>
          {expandedSections.labs && (
            <CardContent className="space-y-3 sm:space-y-4 p-3 sm:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label className="text-primary text-xs sm:text-sm">Renal Function Tests (RFT)</Label>
                  <div className="flex gap-2">
                    <Input 
                      type="number"
                      step="0.01"
                      value={formData.labValues.rft}
                      onChange={(e) => 
                        setFormData(prev => ({
                          ...prev,
                          labValues: { ...prev.labValues, rft: e.target.value }
                        }))
                      }
                      placeholder="Enter value"
                      className="text-xs sm:text-sm"
                    />
                    <span className="flex items-center px-2 sm:px-3 bg-secondary rounded-md text-xs text-muted-foreground whitespace-nowrap">
                      mg/dL
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-primary text-xs sm:text-sm">Hemoglobin (Hb)</Label>
                  <div className="flex gap-2">
                    <Input 
                      type="number"
                      step="0.1"
                      value={formData.labValues.hemoglobin}
                      onChange={(e) => 
                        setFormData(prev => ({
                          ...prev,
                          labValues: { ...prev.labValues, hemoglobin: e.target.value }
                        }))
                      }
                      placeholder="Enter value"
                      className="text-xs sm:text-sm"
                    />
                    <span className="flex items-center px-2 sm:px-3 bg-secondary rounded-md text-xs text-muted-foreground whitespace-nowrap">
                      g/dL
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-primary text-xs sm:text-sm">Total Leukocyte Count (TLC)</Label>
                  <div className="flex gap-2">
                    <Input 
                      type="number"
                      value={formData.labValues.tlc}
                      onChange={(e) => 
                        setFormData(prev => ({
                          ...prev,
                          labValues: { ...prev.labValues, tlc: e.target.value }
                        }))
                      }
                      placeholder="Enter value"
                      className="text-xs sm:text-sm"
                    />
                    <span className="flex items-center px-2 sm:px-3 bg-secondary rounded-md text-xs text-muted-foreground whitespace-nowrap">
                      /μL
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-primary text-xs sm:text-sm">FEV₁ (% predicted)</Label>
                  <div className="flex gap-2">
                    <Input 
                      type="number"
                      step="0.1"
                      value={formData.labValues.fev1}
                      onChange={(e) => 
                        setFormData(prev => ({
                          ...prev,
                          labValues: { ...prev.labValues, fev1: e.target.value }
                        }))
                      }
                      placeholder="Enter value"
                      className="text-xs sm:text-sm"
                    />
                    <span className="flex items-center px-2 sm:px-3 bg-secondary rounded-md text-xs text-muted-foreground whitespace-nowrap">
                      %
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-primary text-xs sm:text-sm">TAC Levels</Label>
                  <div className="flex gap-2">
                    <Input 
                      type="number"
                      step="0.1"
                      value={formData.labValues.tacLevels}
                      onChange={(e) => 
                        setFormData(prev => ({
                          ...prev,
                          labValues: { ...prev.labValues, tacLevels: e.target.value }
                        }))
                      }
                      placeholder="Enter value"
                      className="text-xs sm:text-sm"
                    />
                    <span className="flex items-center px-2 sm:px-3 bg-secondary rounded-md text-xs text-muted-foreground whitespace-nowrap">
                      ng/mL
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-primary text-xs sm:text-sm">Lab Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left text-xs sm:text-sm"
                      >
                        <Calendar className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                        {formData.labValues.labDate ? 
                          format(formData.labValues.labDate, 'PPP') : 
                          'Pick a date'
                        }
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarComponent
                        mode="single"
                        selected={formData.labValues.labDate}
                        onSelect={(date) => 
                          setFormData(prev => ({
                            ...prev,
                            labValues: { ...prev.labValues, labDate: date }
                          }))
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-primary text-xs sm:text-sm">Lab Comments</Label>
                <Textarea 
                  placeholder="Additional comments about lab results..."
                  value={formData.labValues.comments}
                  onChange={(e) => 
                    setFormData(prev => ({
                      ...prev,
                      labValues: { ...prev.labValues, comments: e.target.value }
                    }))
                  }
                  rows={2}
                  className="text-xs sm:text-sm"
                />
              </div>
            </CardContent>
          )}
        </Card>

        {/* Section 6: Doctor's Remarks */}
        <Card className="mb-4 border-l-4 border-l-accent">
          <CardHeader 
            className="cursor-pointer hover:bg-secondary/50 transition-colors p-3 sm:p-6"
            onClick={() => toggleSection('notes')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-accent" />
                <CardTitle className="text-sm sm:text-base">Doctor's Remarks & Notes</CardTitle>
              </div>
              {expandedSections.notes ? 
                <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" /> : 
                <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
              }
            </div>
          </CardHeader>
          {expandedSections.notes && (
            <CardContent className="space-y-3 sm:space-y-4 p-3 sm:p-6">
              <div className="space-y-2">
                <Label className="text-primary text-xs sm:text-sm">Clinical Remarks</Label>
                <Textarea 
                  placeholder="Enter doctor's remarks, follow-up instructions, and additional notes..."
                  value={formData.doctorRemarks}
                  onChange={(e) => setFormData(prev => ({ ...prev, doctorRemarks: e.target.value }))}
                  rows={4}
                  className="text-xs sm:text-sm"
                />
              </div>

              <div className="flex items-center space-x-2 p-3 sm:p-4 rounded-lg bg-secondary/50">
                <input
                  type="checkbox"
                  id="counseled"
                  checked={formData.patientCounseled}
                  onChange={(e) => setFormData(prev => ({ ...prev, patientCounseled: e.target.checked }))}
                  className="w-4 h-4 text-primary"
                />
                <Label htmlFor="counseled" className="text-xs sm:text-sm cursor-pointer">
                  Patient has been counseled about medications and follow-up care
                </Label>
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Confirmation Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-primary">Complete Onboarding</DialogTitle>
            <DialogDescription>
              Are you sure you want to submit this discharge summary? This will complete the onboarding process for {currentPatient.name}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowConfirmModal(false)}>
              Cancel
            </Button>
            <Button className="bg-accent hover:bg-accent/90" onClick={handleConfirm}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Complete Onboarding
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  // ✅ FIX: Conditionally wrap in PatientLayout based on useOwnLayout prop
  if (useOwnLayout) {
    return (
      <PatientLayout 
        currentView="discharge-summary" 
        onNavigate={onNavigate} 
        onLogout={onLogout}
        patientName={currentPatient?.name || 'Patient'}
        adherenceScore={user?.adherence || 0}
      >
        {mainContent}
      </PatientLayout>
    );
  }

  // Return content without layout wrapper (for use within other dashboards)
  return mainContent;
}